// Pure helpers of the transmittal screens (GEDX-FE-02): which documents can be sent, the request
// built from the dialog, acknowledgement counts, and plain-language messages for the API's errors.
import type {
  CreateTransmittalDto,
  DiffusionHistoryDtoOutputItemsItemRecipientsItem,
  TransmittalDtoOutputRecipientsItem,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import {
  TRANSMITTAL_MAX_RECIPIENTS,
  TRANSMITTAL_MAX_VERSIONS,
  type TransmittalLanguage,
} from '@batiflow/contracts';
import { isolate } from '@batiflow/ui';
import type { TFunction } from 'i18next';

export type Translate = TFunction<'transmittals'>;
export type CommonTranslate = TFunction<'common'>;

/** What the dialog needs to know of a document (a row of the list or the open document). */
export interface TransmittalDocument {
  id: string;
  code: string | null;
  title: string;
  revisionIndex: string | null;
  currentVersionId: string | null;
  latestVersionStatus: 'uploading' | 'scanning' | 'available' | 'rejected' | null;
}

/** Only the current (checked, available) version of a document can be sent. */
export const isSendable = (document: TransmittalDocument): boolean =>
  document.currentVersionId !== null;

/** A newer version is still being checked: the index sent is the current one, not that one. */
export const hasNewerBeingChecked = (document: TransmittalDocument): boolean =>
  isSendable(document) &&
  (document.latestVersionStatus === 'uploading' || document.latestVersionStatus === 'scanning');

export function partitionDocuments(documents: readonly TransmittalDocument[]) {
  return {
    sendable: documents.filter(isSendable),
    unavailable: documents.filter((document) => !isSendable(document)),
  };
}

export const MAX_DOCUMENTS = TRANSMITTAL_MAX_VERSIONS;
export const MAX_RECIPIENTS = TRANSMITTAL_MAX_RECIPIENTS;

export interface EmailDraft {
  email: string;
  name: string;
}

export interface PersonOption {
  id: string;
  label: string;
}

export interface DialogValues {
  subject: string;
  message: string;
  language: TransmittalLanguage;
  documents: readonly TransmittalDocument[];
  people: readonly PersonOption[];
  emails: readonly EmailDraft[];
}

/** The request body, with the labels of its versions and recipients in the same order. */
export interface BuiltRequest {
  data: CreateTransmittalDto;
  versionLabels: string[];
  recipientLabels: string[];
}

export function buildRequest(values: DialogValues): BuiltRequest {
  const sendable = values.documents.filter(isSendable);
  const message = values.message.trim();
  return {
    data: {
      subject: values.subject.trim(),
      ...(message ? { message } : {}),
      language: values.language,
      versionIds: sendable.flatMap((document) =>
        document.currentVersionId ? [document.currentVersionId] : [],
      ),
      recipients: [
        ...values.people.map((person) => ({ userId: person.id })),
        ...values.emails.map((draft) => ({
          email: draft.email,
          ...(draft.name.trim() ? { name: draft.name.trim() } : {}),
        })),
      ],
    },
    versionLabels: sendable.map((document) => document.code ?? document.title),
    recipientLabels: [
      ...values.people.map((person) => person.label),
      ...values.emails.map((draft) => draft.email),
    ],
  };
}

/** A same-payload retry reuses the Idempotency-Key; another payload gets a new one. */
export function keyFor(
  previous: { payload: string; key: string } | null,
  data: CreateTransmittalDto,
  newKey: () => string,
): { payload: string; key: string } {
  const payload = JSON.stringify(data);
  return previous?.payload === payload ? previous : { payload, key: newKey() };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmailAddress = (value: string): boolean =>
  value.length <= 254 && EMAIL_PATTERN.test(value);

// --- Acknowledgements ---------------------------------------------------------------------------

type AnyRecipient =
  TransmittalDtoOutputRecipientsItem | DiffusionHistoryDtoOutputItemsItemRecipientsItem;

export const isAcknowledged = (recipient: Pick<AnyRecipient, 'acknowledgedAt'>): boolean =>
  recipient.acknowledgedAt !== null;

/** The recipient row of the signed-in person, when they are one. */
export const recipientOfUser = <R extends Pick<AnyRecipient, 'userId'>>(
  recipients: readonly R[],
  userId: string | null,
): R | null =>
  userId === null ? null : (recipients.find((recipient) => recipient.userId === userId) ?? null);

// --- Errors -------------------------------------------------------------------------------------

export interface SendFailure {
  title: string;
  /** One sentence per problem (a recipient, a document), or the one general sentence. */
  lines: string[];
  traceId: string | undefined;
}

/** `recipients.2` gives 2 (the position in the request); null for any other path. */
const indexOfPath = (path: string, prefix: string): number | null => {
  if (!path.startsWith(`${prefix}.`)) return null;
  const rest = path.slice(prefix.length + 1);
  return /^\d+$/.test(rest) ? Number(rest) : null;
};

/** A plain-language account of a refused sending (never the server's raw text). */
export function sendFailure(
  t: Translate,
  tCommon: CommonTranslate,
  error: unknown,
  request: Pick<BuiltRequest, 'versionLabels' | 'recipientLabels'>,
): SendFailure {
  const base = { title: t('errors.failedTitle') };
  if (!(error instanceof ApiError)) {
    return { ...base, lines: [t('errors.generic')], traceId: undefined };
  }
  const traceId = error.traceId;
  if (error.status === 422 && error.fieldErrors.length > 0) {
    const lines = error.fieldErrors.map((field) => {
      const recipient = indexOfPath(field.path, 'recipients');
      if (recipient !== null) {
        const known = [
          'RECIPIENT_CANNOT_ENTER',
          'RECIPIENT_DOMAIN_NOT_ALLOWED',
          'RECIPIENT_DUPLICATE',
        ];
        const key = known.includes(field.code) ? field.code : 'other';
        return t(`errors.recipient.${key}`, {
          name: isolate(request.recipientLabels[recipient] ?? ''),
        });
      }
      const version = indexOfPath(field.path, 'versionIds');
      if (version !== null) {
        const key =
          field.code === 'VERSION_NOT_AVAILABLE' || field.code === 'VERSION_NOT_FOUND'
            ? field.code
            : 'other';
        return t(`errors.version.${key}`, {
          name: isolate(request.versionLabels[version] ?? ''),
        });
      }
      return t('errors.validation');
    });
    return { ...base, lines: [...new Set(lines)], traceId };
  }
  if (error.code === 'VALIDATION_FAILED') {
    return { ...base, lines: [t('errors.validation')], traceId };
  }
  if (error.status === 409) return { ...base, lines: [t('errors.conflict')], traceId };
  if (error.status === 403 || error.status === 404 || error.code === 'FORBIDDEN') {
    return { ...base, lines: [t('errors.forbidden')], traceId };
  }
  // Codes with a sentence of their own in the shared list (read-only account, rate limit…).
  const shared = tCommon(`errors.${error.code}`, { defaultValue: '' });
  return { ...base, lines: [shared || t('errors.generic')], traceId };
}
